import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PaymentErrorScreenComponent } from '@app/modules/setting/dialogs/payment-error-screen/payment-error-screen.component';
import { AskSecurityKeyPasswordComponent } from '@app/modules/vault/components/dialogs/ask-security-key-password/ask-security-key-password.component';
import { loaderTypes } from '@app/shared/component/loader/loader';
import { environment } from '@env/environment';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AmplifyHttpClient } from '../auth/http.amplify-httpclient';
import { CryptoService } from '../crypto/crypto.services';
import { FileListUpdate } from '../file-list-update/fileListUpdate.service';
import { DocumentType, DocumentTypeConverter } from './document-type.interface';
import { UserDocument, UserDocumentConverter } from './user-document.interface';
import * as fileSaver from 'file-saver';
import { Store } from '@ngrx/store';
import { vaultStoreActions } from '@app/root-store';
import { Router } from '@angular/router';
import { ColoborationManagementAdmin } from '../coloboration-management-admin/coloboration-management-admin.service';
import { MovedToSharedVault } from '../moved-to-shared-vault/moved-to-shared-vault.service';
@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  subscription = new Subscription();
  constructor(
    private dialog: MatDialog,
    private fileListUpdate: FileListUpdate,
    private readonly httpService: AmplifyHttpClient,
    private store: Store,
    private movedToSharedVault: MovedToSharedVault,
    private router: Router,
    private converter: DocumentTypeConverter,
    private userDocConverter: UserDocumentConverter,
    private coloborationManagementAdmin: ColoborationManagementAdmin,
    private cryptoService: CryptoService
  ) {}

  private popupOpened = new Subject<any>();
  private closeDropdowns = new Subject<any>();

  getDocumentTypes(): Observable<DocumentType[]> {
    const url = `api/documents/types`;
    return this.httpService.get(url).pipe(map((response: any) => response.documentTypes.map((item: any) => this.converter.convert(item))));
  }
  getUserDocuments(subTypeId: number): Observable<UserDocument[]> {
    const url = `api/documents/${subTypeId}`;
    return this.httpService.get(url).pipe(map((response: any) => response.documents.map((item: any) => this.userDocConverter.convert(item))));
  }
  uploadDocument(subTypeId: number, payload: FormData): Observable<UserDocument> {
    const url = `api/documents/upload/${subTypeId}`;
    return this.httpService.post(url, payload).pipe(map((respone: any) => this.userDocConverter.convert(respone.document)));
  }
  getLinkedItemForDropdown(userId) {
    const url = `api/task/task-not-linked-form/${userId}`;
    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getFileActivityLog(id) {
    const url = `api/vault/vault-user-form-activity/${id}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getFolderActivityLog(id) {
    const url = `api/vault/vault-user-folder-activity/${id}`;
    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getFormLinkingSuggestions(formId, type = 'linking') {
    const url = `api/vault/form-linking-suggestions/${formId}/${type}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          if (response.data?.length && type == 'linking') {
            return response.data?.filter((x) => x.suggested_vault_form_id != null || x.suggested_vault_user_form_id != null);
          } else {
            return response.data;
          }
        }
      })
    );
  }
  getSharedWithMeFolders() {
    const url = `api/vault/shared-with-me-users`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getSharedByme() {
    const url = `api/vault/shared-by-me-users`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  // getSharedByMeFolders(id, vaultId = null) {
  //   let url = `api/vault/shared-by-me-users/${id}`;
  //   if (vaultId && id != vaultId) {
  //     url = url + `?vault_id=${vaultId}`;
  //   }

  //   return this.httpService.get(url).pipe(
  //     map((response: any) => {
  //       if (response.status == 200) {
  //         return response;
  //       }
  //     })
  //   );
  // }

  removeDefaultVaultFolders(request) {
    const url = `api/vault/default-folder-ignore-user/`;
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getDefaultFolderForMyData() {
    const url = `api/vault/my-data-count`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getMyDataStatus() {
    const url = `api/vault/my-data-status`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getOfficeAdminList(id) {
    return this.httpService.get<any>(`api/vault/vault-list-admin/${id}`, loaderTypes.none).pipe(
      map((response) => {
        if (response.status == 200) {
          if (response.files.length > 0) {
            this.store.dispatch(new vaultStoreActions.SetVaultSuccessAction(response.files[0].id, response.files[0].vault_name));
            if (response.files[0].id != null) {
              // this.subscription.add(
              //   this.movedToSharedVault.sendMessage({
              //     vault: response.files[0],
              //     isTrue: true,
              //   })
              // );
            }
          }
          return response.files;
        } else {
          return response;
        }
      })
    );
  }

  getContactsFormByUserId(id) {
    return this.httpService.get<any>(`api/vault/forms-user-contacts/${id}`, null, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getFolderForms(id) {
    return this.httpService.get<any>(`api/vault/folder-forms/${id}`, null, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getFolderForms2() {
    return this.httpService.get<any>(`api/vault/folder-forms`, null, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createFormEncryption(payload, vault_user_form_id, selectedSecurity_key = null) {
    let encryption_key;
    const noteTitle = JSON.parse(payload.note).title ?? '';
    if (selectedSecurity_key == null) {
      encryption_key = this.cryptoService.generateEncryptionKey(payload.user_pk, null, null);
    } else {
      encryption_key = this.cryptoService.generateEncryptionKey(payload.user_pk, selectedSecurity_key);
    }

    payload.note = this.cryptoService.encryptData(JSON.stringify(payload.note), encryption_key);
    return this.createNote({
      vault_user_form_id: vault_user_form_id,
      note: JSON.stringify(payload.note),
      title: noteTitle,
      ...(payload.suggestion_note_type && { suggestion_note_type: payload.suggestion_note_type }),
      ...(payload.suggestion_note_status && { suggestion_note_status: payload.suggestion_note_status }),
    });
  }
  updateFormTampletData(payload, sk_value, last_used_sk_value, form_encryption_key, sk_just_enabled, sk, Showloader = false) {
    try {
      let encryption_key;
      //generate a new key if
      // 1) no previous key is found
      // 2) premium security just disabled
      // 3) premium security just enabled

      if (!form_encryption_key || sk_just_enabled) {
        //generate a new encryption key to encrypt form data
        encryption_key = this.cryptoService.generateEncryptionKey(payload.user_pk, sk_value, form_encryption_key);
      } else if (last_used_sk_value) {
        //switch encryption type on form encryption key
        encryption_key = this.cryptoService.generateEncryptionKey(
          payload.user_pk,
          sk_value,
          this.cryptoService.decryptEncryptionKey(form_encryption_key, last_used_sk_value, sk)
        );
      } else {
        if (sk) {
          encryption_key = {
            plain: this.cryptoService.decryptEncryptionKey(form_encryption_key, sk_value, sk),
            encrypted: form_encryption_key,
          };
        } else {
          encryption_key = this.cryptoService.generateEncryptionKey(payload.user_pk, null, form_encryption_key);
        }
      }

      const propertList = Object.keys(payload.form_data.dynamicFieldsForm);
      let encryptionRequired = false;

      propertList.forEach((element) => {
        if (
          payload.form_data.dynamicFieldsForm[element] != null &&
          payload.form_data.dynamicFieldsForm[element] != undefined &&
          payload.form_data.dynamicFieldsForm[element] != ''
        ) {
          encryptionRequired = true;
        }
      });
      payload.form_data.beneFicaryForm.forEach((rootelement) => {
        let propertList = Object.keys(rootelement);
        const childData = rootelement[propertList.toString()];
        childData.forEach((childelement) => {
          const childpropertList = Object.keys(childelement);
          childpropertList.forEach((subChildelement) => {
            if (childelement[subChildelement] != null && childelement[subChildelement] != undefined && childelement[subChildelement]?.length) {
              encryptionRequired = true;
            }
          });
        });
      });

      //encrypt form data before sending it to the server
      if (encryptionRequired) {
        payload.form_data = this.cryptoService.encryptData(JSON.stringify(payload.form_data), encryption_key);
      } else {
        payload.form_data = null;
      }
      if (payload.notes == null || payload.notes == undefined || payload.notes == '') {
        payload.notes = null;
      } else {
        payload.notes = JSON.stringify(this.cryptoService.encryptData(payload.notes ?? '', encryption_key));
      }
      if (payload.physical_location == null || payload.physical_location == undefined || payload.physical_location == '') {
        payload.physical_location = null;
      } else {
        payload.physical_location = JSON.stringify(this.cryptoService.encryptData(payload.physical_location ?? '', encryption_key));
      }

      payload.encryption_key = encryption_key.encrypted;

      if (sk_value || last_used_sk_value) {
        //add encrypted encryption key to payload
        sk_value = sk_value ?? last_used_sk_value; //ensure we send values to server if form is currently permium secured or just disabled to handled file crypto work server side.
        if (sk) {
          encryption_key = {
            plain: this.cryptoService.decryptEncryptionKey(form_encryption_key, sk_value, sk),
            encrypted: form_encryption_key,
          };
        } else if (!sk_just_enabled) {
          encryption_key = this.cryptoService.generateEncryptionKey(payload.user_pk, null, form_encryption_key);
        }
        payload.encrypted_encryption_key = this.cryptoService.encryptWithPublicKey(encryption_key.plain, environment.crypto_public_key);
        payload.security_key_details = JSON.stringify(sk_value);
      }

      delete payload['user_pk'];
      return this.httpService.post(`api/vault/forms-user`, payload, Showloader ? loaderTypes.mainAnimation : loaderTypes.none).pipe(
        map((response: any) => {
          if (response.status == 200 || response.status == 201) {
            this.fileListUpdate.sendMessage('true');
            if (response?.data?.limit_exceeded) {
              const data: HttpErrorResponse = new HttpErrorResponse({ error: 'Data incorrect', status: 400 });

              this.dialog.open(PaymentErrorScreenComponent, {
                panelClass: 'full-window-dialog-2',
                autoFocus: false,
                disableClose: true,
                data: response?.data,
              });

              return this.handleAuthError(data);
            } else {
              return response.data;
            }
          } else {
            return response;
          }
        })
      );
    } catch (error) {
      console.error('🚀 ~ DocumentService ~ updateFormTampletData ~ error:', error);
    }
  }
  getUslefileListCount(id) {
    return this.httpService.get<any>(`api/vault/vault-stats/${id}`, loaderTypes.none).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultFormesTag(tag, parent_location_id = null) {
    const url = `api/vault/vault-forms-tag`;
    const request = {
      tag: tag,
      parent_location_id: parent_location_id,
      vault_user_form_id: null,
    };
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        }
      })
    );
  }

  updateFormSuggestion(request) {
    const url = `api/vault/forms-user-ignore-suggestion`;
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        }
      })
    );
  }

  addDefaultVaultFolders(request) {
    const url = `api/vault/folders-user`;
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  removeVaultFolderFile(request) {
    const url = `api/vault/default-form-ignore-user`;
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  postCreatePresignedUrl(request, showpopup, Showloader = true) {
    const url = `api/vault/form-upload-file`;
    return this.httpService.post(url, request, Showloader ? loaderTypes.default : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200 || response.status == 201) {
          if (response?.data?.limit_exceeded) {
            if (showpopup) {
              const data: HttpErrorResponse = new HttpErrorResponse({ error: 'Data incorrect', status: 400 });

              this.dialog.open(PaymentErrorScreenComponent, {
                panelClass: 'full-window-dialog-2',
                autoFocus: false,
                disableClose: true,
                data: response?.data,
              });
              return this.handleAuthError(data);
            } else {
              const data: HttpErrorResponse = new HttpErrorResponse({ error: response?.data, status: 400 });
              return this.handleAuthError(data);
            }
          } else {
            return response.data;
          }
        } else {
          return response;
        }
      })
    );
  }

  getdownloadAllDocumentsByFolder(userId, folderId) {
    const encrypturl = `api/vault/check-vault-user-folder-encrypted/${userId}/${folderId}`;
    const url = `api/vault/vault-user-folder-download-url/${userId}/${folderId}`;
    return this.httpService.get(encrypturl).pipe(
      mergeMap(async (response: any) => {
        if (response.status == 200) {
          if (response?.data.securityKey) {
            return await new Promise((resolve) => {
              const data = { security_key: response?.data.security_key };
              const modalRef2 = this.dialog.open(AskSecurityKeyPasswordComponent, {
                data: {
                  currentuserId: userId,
                  currentValutUserId: userId,
                  folderId: folderId,
                  isDownloadPopup: false,
                  data: data,
                },
                disableClose: true,
                panelClass: 'full-window-dialog-2',
                autoFocus: false,
              });

              this.subscription.add(
                modalRef2.afterClosed().subscribe((results) => {
                  const payload: any = {};
                  payload.security_key_details = results.security_key;
                  payload.sk = this.cryptoService.encryptWithPublicKey(results.sk, environment.crypto_public_key);
                  this.subscription?.unsubscribe();
                  return this.httpService.post_promise(url, payload).then((res: any) => resolve(res));
                })
              );
            });
          } else {
            return this.httpService.post_promise(url, {}).then((res: any) => res);
          }
        } else {
          return response;
        }
      })
    );
  }

  postFiletoAwsbucket(url, request) {
    return this.httpService.put(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getPersonalFiles(userId, folderId, mydata = false) {
    let w_id = this.getWorSpaceId();
    let vaultId = this.getVaultId();
    let url = '';
    if (folderId == null || folderId === undefined) {
      url = `api/vault/forms-user-folder/${userId}`;
    } else {
      url = `api/vault/forms-user-folder/${userId}/${folderId}`;
    }
    if (mydata) {
      url = url + '?myData=true';
      if (w_id != null) {
        url = url + `&w_id=${w_id}`;
      }
      if (vaultId != null) {
        url = url + `&vaultid=${vaultId}`;
      }
    } else {
      if (vaultId != null) {
        url = url + `?vaultid=${vaultId}`;
      }
    }

    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getEntitiesForSync(userId: string, searchText: string = '', vaultId?: string) {
    let url = `api/vault/forms-user-folder/${userId}/Entities?myData=true`;
    const targetVaultId = vaultId ?? this.getVaultId();
    if (targetVaultId) {
      url = url + `&vaultid=${targetVaultId}`;
    }
    if (searchText && searchText.trim().length) {
      url = url + `&search=${encodeURIComponent(searchText.trim())}`;
    }

    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          const files = response?.data?.files ?? response?.data ?? [];
          return files;
        } else {
          return [];
        }
      })
    );
  }

  moveFileToFolder(request) {
    const url = `api/vault/move-form`;
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getSharedDataWithMe(userId, sharingUserId) {
    const url = `api/vault/folders-files-user-shared-with-me/${userId}/${sharingUserId}`;
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  attachmentAIProcess(payload) {
    const url = `api/vault/inbox-file-ai-process`;
    return this.httpService.patch(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return {
            ...response,
            payload,
          };
        } else {
          return response;
        }
      })
    );
  }

  disableAutoNaming(payload) {
    const url = `api/vault/forms-user-form-auto-naming`;
    return this.httpService.patch(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return {
            ...response,
            payload,
          };
        } else {
          return response;
        }
      })
    );
  }
  saveNameWithAction(data, ShowLoader = false) {
    const payload = {
      id: data.id,
      user_id: data.user_id,
      form_name: '',
      document_name: data.documentName,
    };

    const generalInformationName = this.GeneralInformationName(data);
    if (data.naming_convention == 'General Information') {
      payload['form_name'] = generalInformationName;
      if (!generalInformationName) {
        payload['form_name'] = data.form_name;
      }
    } else if (data.naming_convention == 'General Information (Vault Name)') {
      let name = '';
      try {
        name = data.vault_list.find((x) => x.id == data.vault_id).name;
      } catch (error) {}
      payload['form_name'] = generalInformationName ? generalInformationName + ' (' + name + ')' : name;
    } else if (data.naming_convention == 'General Information + Template Name') {
      payload['form_name'] = generalInformationName ? generalInformationName + ' ' + data.current_template.type : data.current_template.type;
    } else if (data.naming_convention == 'General Information (Template Name)') {
      payload['form_name'] = generalInformationName ? generalInformationName + ' (' + data.current_template.type + ')' : data.current_template.type;
    } else if (data.naming_convention == 'Template Name + General Information') {
      payload['form_name'] = generalInformationName ? data.current_template.type + ' ' + generalInformationName : data.current_template.type;
    } else if (data.naming_convention == 'Template Name + (General Information)' || data.naming_convention == 'Template Name (General Information)') {
      payload['form_name'] = generalInformationName
        ? data.current_template.type + ' (' + generalInformationName + ')'
        : data.form_name
        ? data.form_name
        : data.current_template.type;
    } else if (data.naming_convention == 'Nickname + General Information') {
      payload['form_name'] = generalInformationName;
      payload['additional_data'] = 'Nickname';
    } else if (data.naming_convention == `Nickname's + General Information`) {
      payload['form_name'] = generalInformationName;
      payload['additional_data'] = `Nickname's`;
    } else if (data.naming_convention == `Nickname's + General Information + Template Name`) {
      //Nickname's + General Information + Template Name
      payload['form_name'] = generalInformationName ? generalInformationName + ' ' + data.current_template.type : data.current_template.type;
      payload['additional_data'] = `Nickname's`;
    } else if (data.naming_convention == `General Information + Template Name (Vault Name)`) {
      let name = '';
      try {
        name = `(${data.vault_list.find((x) => x.id == data.vault_id).name})`;
      } catch (error) {}
      let form_name = generalInformationName + ' ' + data.current_template.type + ' ' + name;
      payload['form_name'] = form_name;
    } else if (true) {
      // Nickname + Template Name
      return;
    }
    const url = `api/vault/forms-user-form-name`;
    return this.httpService.patch(url, payload, ShowLoader ? loaderTypes.mainAnimation : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return {
            ...response,
            payload,
          };
        } else {
          return response;
        }
      })
    );
  }
  GeneralInformationName(data) {
    let name = '';
    data.filterArrayItems.forEach((element, index) => {
      if (data.dynamicFieldsForm[element.formControlName].length) {
        name = name + data.dynamicFieldsForm[element.formControlName] + ' ';
      }
    });
    return name.trim();
  }
  saveFormNameInDb(data, ShowLoader = false) {
    const url = `api/vault/forms-user-form-name`;
    return this.httpService.patch(url, data, ShowLoader ? loaderTypes.mainAnimation : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateFormIsStarred(data: { id: string; is_starred: boolean }, ShowLoader = false) {
    const url = `api/vault/forms-user-form-is_starred`;
    return this.httpService.patch(url, data, ShowLoader ? loaderTypes.mainAnimation : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  ignoreAsDuplicate(data: { id: string | number; ignore_as_duplicate: boolean }, ShowLoader = false) {
    const url = `api/vault/forms-user-form-ignore-as-duplicate`;
    return this.httpService.patch(url, data, ShowLoader ? loaderTypes.mainAnimation : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  getformTempletByid(fid, id) {
    const url = `api/vault/forms-user/${fid}/${id}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  saveDataExtractionUpdateById(data) {
    const url = `api/data-extraction/data-extraction-update-by-id`;
    return this.httpService.patch(url, data).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  getformConnectionStatusById(fid, id) {
    const url = `api/vault/form-connection-status/${fid}/${id}`;
    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  transformContact(data: { vault_user_form_id: string; user_id: string }) {
    const url = `api/vault/transform-contact`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  transformPlaceholder(data: { vault_user_form_id: string; new_vault_user_form_id: string }) {
    const url = `api/vault/transform-placeholder`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deleteAttachedFileWithReminder(reminder_id) {
    const url = `api/vault/remove-attached-file-with-reminder/${reminder_id}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deletUserUploadedFile(user_id, file_id) {
    const url = `api/vault/form-upload-file/${user_id}/${file_id}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  // getlinkFileDownalod(user_id, file_id, form_id = null) {
  //   let url = `api/vault/vault-user-form-file-download-url/${user_id}/${file_id}`;
  //   if (form_id) {
  //     url = `api/vault/vault-user-form-download-url/${user_id}/${form_id}`;
  //   }
  //   return this.httpService.get(url, loaderTypes.none).pipe(
  //     map((response: any) => {
  //       if (response.status == 200) {
  //         return response;
  //       } else {
  //         return response;
  //       }
  //     })
  //   );
  // }
  updateFileUploadTriggerAI(request, Showloader = true) {
    const url = `api/vault/form-upload-file-ai-trigger`;
    return this.httpService.patch(url, request, Showloader ? loaderTypes.default : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  setFileuploadedStatus(request, Showloader = true) {
    const url = `api/vault/form-upload-file`;
    return this.httpService.patch(url, request, Showloader ? loaderTypes.default : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  downloadDocumentOrAttachment(userid, fileid, formid, security_key, sk, name) {
    this.subscription.add(
      this.downloadVaultFile(userid, fileid, formid).subscribe((res) => {
        let decrypted_file;
        let decryption_key;
        if ((formid && res.data.key == null) || res.data.premium_encrypted == null) {
          decrypted_file = res.data.data; //file is not encrypted. we send pre signed url in the data
          //we pass url to file saver directly. no need to create blob
          fetch(decrypted_file)
            .then((res) => res.blob())
            .then((blob) => fileSaver.saveAs(blob, name));
        } else {
          if (res.data.premium_encrypted == 'encrypted' || res.data.premium_encrypted == 'pending_decryption') {
            //decrypt file using user's security key
            decryption_key = this.cryptoService.decryptDataKey(res.data.key, security_key, sk);
            decrypted_file = this.cryptoService.decryptFile(res.data.data, decryption_key);
          } else {
            //get key to decrypt file
            const decryption_key = this.cryptoService.decryptWithPassword(res.data.key, environment.encryption_mask_id);
            decrypted_file = this.cryptoService.decryptFile(res.data.data, decryption_key);
          }
          if (res.data.premium_encrypted == 'url' && res.data.data.includes('https://s3.amazonaws.com/')) {
            let xhr = new XMLHttpRequest();
            xhr.open('GET', res.data.data, true);
            xhr.responseType = 'blob';
            xhr.onload = () => {
              let reader = new FileReader();
              reader.onload = (event: any) => {
                this.saveDonwoLoadFileFromLink(security_key, sk, name, event.target.result, res);
              };
              let file1 = xhr.response;
              reader.readAsText(file1);
              // reader.readAsDataURL(file1);
            };
            xhr.send();
          } else {
            const blob = new Blob([decrypted_file], { type: 'application/octet-stream' });
            fileSaver.saveAs(blob, name);
          }
        }
      })
    );
  }
  saveDonwoLoadFileFromLink(security_key, sk, name, fileData, res) {
    let decryption_key, decrypted_file;
    if (res.data.premium_encrypted == 'encrypted' || res.data.premium_encrypted == 'pending_decryption') {
      //decrypt file using user's security key
      decryption_key = this.cryptoService.decryptDataKey(res.data.key, security_key, sk);
      decrypted_file = this.cryptoService.decryptFile(fileData, decryption_key);
    } else {
      //get key to decrypt file
      const decryption_key = this.cryptoService.decryptWithPassword(res.data.key, environment.encryption_mask_id);
      decrypted_file = this.cryptoService.decryptFile(fileData, decryption_key);
    }
    const blob = new Blob([decrypted_file], { type: 'application/octet-stream' });
    fileSaver.saveAs(blob, name);
  }
  getUploadedFilesListbyformId(folder_id, form_id) {
    const url = `api/vault/vault-user-form-uploads/${folder_id}/${form_id}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getReminderList(user_id, form_id, allEnable = false) {
    const url = allEnable
      ? `api/vault/vault-user-form-reminder/${user_id}/${form_id}/all`
      : `api/vault/vault-user-form-reminder/${user_id}/${form_id}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateReminderActiveStatus(request) {
    const url = `api/vault/vault-user-form-reminder-active-status`;
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  deleteReminder(user_id, id) {
    const url = `api/vault/vault-user-form-reminder/${user_id}/${id}`;
    return this.httpService.delete(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getUserLocationList(userid, vaultId = null) {
    let url = `api/vault/vault-user-all-location/${userid}`;
    if (vaultId != null) {
      url = url + `?vault_id=${vaultId}`;
    }

    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.files;
        } else {
          return response;
        }
      })
    );
  }

  getVaultUserFormOwnedItems(userId, formId, source?: string) {
    const url = source
      ? `api/vault/vault-user-form-owned-items/${userId}/${formId}/${source}`
      : `api/vault/vault-user-form-owned-items/${userId}/${formId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormOwnedItems(payload) {
    const url = `api/vault/delete-vault-user-form-owned-items`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultUserFormRolePlayers(request) {
    const url = `api/vault/get-vault-user-form-role-players`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createVaultUserFormRolePlayers(request) {
    const url = `api/vault/vault-user-form-role-players`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultUserFormRolePlayers(request) {
    const url = `api/vault/vault-user-form-role-players`;
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormRolePlayers(request) {
    const url = `api/vault/delete-vault-user-form-role-players`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createVaultUserFormRolePlayersOwnership(request) {
    const url = `api/vault/vault-user-form-role-players-ownership`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultUserFormRolePlayersOwnership(request) {
    const url = `api/vault/vault-user-form-role-players-ownership`;
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormRolePlayersOwnership(request) {
    const url = `api/vault/delete-vault-user-form-role-players-ownership`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormRolePlayersOwnershipCounterparty(userId, counterpartyId) {
    const url = `api/vault/vault-user-form-role-players-ownership-counterparty/${userId}/${counterpartyId}`;
    return this.httpService.delete(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormRolePlayersOwnershipFiles(userId, fileId) {
    const url = `api/vault/vault-user-form-role-players-ownership-file/${userId}/${fileId}`;
    return this.httpService.delete(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultUserFormRolePlayersSuggestion(userId, formId) {
    const url = `api/vault/vault-user-form-role-players-suggestion/${userId}/${formId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormRolePlayersSuggestion(request) {
    const url = `api/vault/delete-vault-user-form-role-players-suggestion`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteSharedFiles(id = []) {
    const url = `api/vault/share-form-ids`;
    return this.httpService
      .delete(
        url,
        {
          id: id,
        },
        loaderTypes.mainAnimation
      )
      .pipe(
        map((response: any) => {
          if (response.status == 200) {
            return response;
          } else {
            return response;
          }
        })
      );
  }

  getSubLocationOptionsList(userid, vaultId = null) {
    let route = `api/vault/vault-user-all-location/${userid}`;
    if (vaultId != null) {
      route = route + `?vault_id=${vaultId}`;
    }

    return this.httpService.get<any>(route).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.files;
        } else {
          return response;
        }
      })
    );
  }

  getFormUserFormSubLocation(userid, vaultuserformid, vaultId = null) {
    let url = `api/vault/vault-user-form-sub-location/${userid}/${vaultuserformid}`;
    if (vaultId != null) {
      url = url + `?vault_id=${vaultId}`;
    }

    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.files;
        } else {
          return response;
        }
      })
    );
  }
  getFormUserFormAssociation(userid, vaultuserformid, vaultId = null) {
    let url = `api/vault/forms-user-association/${userid}/${vaultuserformid}`;
    if (vaultId != null) {
      url = url + `?vault_id=${vaultId}`;
    }

    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateDeactvateSuggestion(id, isLoadingRow = true) {
    const url = `api/vault/vault-user-inbox-suggestion-deactivate/${id}`;
    return this.httpService.patch(url, {}, isLoadingRow ? loaderTypes.mainAnimation : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  createUserFormAssociationMultiple(request, isLoadingRow = true) {
    const url = `api/vault/form-association-multiple`;
    return this.httpService.post(url, request, isLoadingRow ? loaderTypes.mainAnimation : loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateFormsUserIgnoreSuggestion(request) {
    const url = `api/vault/forms-user-ignore-suggestion`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  createReProcessAiUploads(request) {
    const url = `api/vault/re-process-ai-uploads`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  createUserFormAssociation(request) {
    const url = `api/vault/form-association`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  removePassword(request) {
    const url = `api/vault/remove-password-user-form-upload`;
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getFormSummarySummaryData(id) {
    const url = `api/vault/vault-user-form-summary/${id}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getUserFormAssociation(userId, FormId) {
    const url = `api/vault/form-association/${userId}/${FormId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteUserFormAssociation(userId, FormId, associatedformid, vault_user_form_uploads_id = null) {
    let url = `api/vault/form-association/${userId}/${FormId}/${associatedformid}`;
    if (vault_user_form_uploads_id != null && vault_user_form_uploads_id != undefined) {
      url = url + '/' + vault_user_form_uploads_id;
    }
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deleteUserMultipleFormAssociation(userId, vault_user_form_id, associatedformid) {
    let url = `api/vault/delete-form-association-multiple`;
    return this.httpService
      .post(url, {
        vault_user_form_id: vault_user_form_id,
        user_id: userId,
        associate_vault_user_form_id: associatedformid,
      })
      .pipe(
        map((response: any) => {
          if (response.status == 200) {
            return response;
          } else {
            return response;
          }
        })
      );
  }
  deleteVaultUserLocation(userId, id) {
    const url = `api/vault/vault-user-location/${userId}/${id}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deleteUserForm(userId, FormId, associatedformid) {
    const url = `api/vault/form-association/${userId}/${FormId}/${associatedformid}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateLocationKey(data) {
    const url = `api/vault/vault-user-location-key-code-required`;
    return this.httpService.post(url, data).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  createRequestFile(request) {
    const url = `api/vault/request-file`;
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultFile(userId, fileId) {
    const url = `api/vault/forms-user/${userId}/${fileId}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          this.fileListUpdate.sendMessage('true');
          return response;
        }
      })
    );
  }

  deleteVaultFolder(userId, folderId) {
    const url = `api/vault/folders-user/${userId}/${folderId}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          this.fileListUpdate.sendMessage('true');
          return response;
        }
      })
    );
  }

  getAdminAccess() {
    const url = `api/vault/vault-admins`;

    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getSharedFilesinFolders(userId, sharingUserId, vaultUserFolderId) {
    const url = `api/vault/files-in-folder-user-shared-with-me/${userId}/${sharingUserId}/${vaultUserFolderId}`;
    return this.httpService.get(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getdownloadAllDocumentsProgress(userId, guid) {
    const url = `api/vault/vault-user-folder-download-progress/${userId}/${guid}`;
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  saveDataExtractionForm(data, loaderType = loaderTypes.mainAnimation) {
    const url = `api/vault/vault-user-form-from-extraction`;
    return this.httpService.post(url, data, loaderType).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        }
      })
    );
  }
  getforIdForFirstUploadDocument(data, loaderType = loaderTypes.none) {
    const url = `api/vault/new-vault-user-form-id`;
    return this.httpService.post(url, data, loaderType).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }
  getformStatusById(userid, formid, loaderType = loaderTypes.mainAnimation) {
    const url = `api/vault/form-status/${userid}/${formid}`;
    return this.httpService.get(url, loaderType).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        }
      })
    );
  }
  saveformDataForChecklistSetup(data, loaderType = loaderTypes.none) {
    const url = `api/vault/forms-user`;
    return this.httpService.post(url, data, loaderType).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  openPopup(popup) {
    this.popupOpened.next(popup);
  }

  getPopup() {
    return this.popupOpened.asObservable();
  }

  getVaultRecentForms(userId) {
    const url = `api/vault/vault-forms-recent/${userId}`;
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  addSubfolder(request) {
    const url = 'api/vault/subfolder-user';

    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  addGroup(request) {
    const url = 'api/vault/group';

    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateFormGroupId(request) {
    const url = 'api/vault/update-form-group-id';
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  updateFolderIdGroup(request) {
    const url = 'api/vault/move-group';
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  updateGroup(request) {
    const url = 'api/vault/group';

    return this.httpService.patch(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateSubfolderName(request) {
    return this.httpService.patch<any>(`api/vault/subfolder-user`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAllSubfolders(userId, vaultUserFolderId, mydata = false) {
    let w_id = this.getWorSpaceId();
    let url = `api/vault/subfolder-user/${userId}/${vaultUserFolderId}`;
    if (mydata) {
      url = url + '?myData=true';
      if (w_id != null) {
        url = url + `&w_id=${w_id}`;
      }
    }
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }
  deleteGroup(user_id, id) {
    const url = `api/vault/group/${user_id}/${id}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  moveItemToFolder(request) {
    const url = `api/vault/subfolder-move`;
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getAllUnsharedFiles(userId, vaultUserFolderId) {
    const url = `api/vault/forms-user-folder-all-unshared/${userId}/${vaultUserFolderId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getAllUnsharedFolders(userId, vaultUserFolderId) {
    const url = `api/vault/subfolders-user-folder-all-unshared/${userId}/${vaultUserFolderId}`;
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }
  getAdminsForDelegation(userFolderId, checklistId) {
    return this.httpService.get<any>(`api/permission/admins-for-checklist-delegation/${userFolderId}/${checklistId}`, loaderTypes.none).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultCategories() {
    return this.httpService.get<any>(`api/common/vault-categories`, null, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  setSetupFolder(request) {
    const url = `api/vault/category-setup`;
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  verifySecurityKey(request) {
    const url = `api/user/verify-security-key`;
    return this.httpService.post(url, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  async generateKeyAsync(request) {
    return await this.cryptoService.generateDataKeyPairAsync(request);
  }

  createSecuritykey(request) {
    const url = `api/user/security-key`;

    request.key = this.cryptoService.hashData(request.key);
    return this.httpService.post(url, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  /**
   * manage errors
   * @param err
   * @returns {any}
   */
  private handleAuthError(err: HttpErrorResponse): Observable<any> {
    //handle your auth error or rethrow
    throw err;
  }

  getSecurityKey() {
    const url = `api/user/security-key`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getSecurityKeyWidhId(id, w_id = null) {
    let url = `api/user/security-key`;
    if (w_id != null) {
      url = url + `/${w_id}`;
    }
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getSecurityKeyWidhIdForVaultForm(id) {
    const url = `api/user/security-key/${id}/form`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200 || response.status == 201) {
          if (response?.data?.limit_exceeded) {
            let data: HttpErrorResponse = new HttpErrorResponse({ error: 'Data incorrect', status: 400 });
            const modalRef3 = this.dialog.open(PaymentErrorScreenComponent, {
              panelClass: 'full-window-dialog-2',
              autoFocus: false,
              disableClose: true,
              data: response?.data,
            });
            return this.handleAuthError(data);
          } else {
            return response.data;
          }
        } else {
          return response;
        }
      })
    );
  }

  getFolderListForMyInformation(Id) {
    const url = `api/office-vault/folder-all/${Id}`;
    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getworkSpaceFolder(Id) {
    const url = `api/office/workspace-folder-all/${Id}`;
    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getSecurityKeyByFormId(formId) {
    const url = `api/vault/form-security-key/${formId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteSecurityKey(request) {
    const url = `api/user/delete-security-key`;
    request.key = this.cryptoService.hashData(request.key);
    return this.httpService.post(url, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultDistributor(vaultId = null) {
    let url = `api/permission/distributors`;
    if (vaultId != null) {
      url = url + `?vault_id=${vaultId}`;
    }

    return this.httpService.get(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  downloadVaultFile(userid, fileid, formid = null) {
    let url = `api/vault/vault-user-form-file-download/${userid}/${fileid}`;
    if (formid) return;
    return this.httpService.get(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  checkVaultFileEncryption(userid, formid, security_key) {
    //generate an encryption key that will be used to encrypt files on the server side
    const encryption_key = this.cryptoService.generateEncryptionKey(null, security_key);
    const encrypted_encryption_key = this.cryptoService.encryptWithPublicKey(encryption_key.plain, environment.crypto_public_key);

    const request = {
      security_key_details: JSON.stringify(security_key),
      encrypted_encryption_key: JSON.stringify(encrypted_encryption_key),
    };
    return this.httpService.post(`api/vault/vault-user-form-file-check-encryption/${userid}/${formid}`, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getDeleteSecurityKeyProgress(userid, jobid) {
    return this.httpService.get(`api/user/delete-security-key-progress/${userid}/${jobid}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  gatLatestBalance(userid, formid, type?: string) {
    return this.httpService.get(`api/Vault/latest-balance/${userid}/${formid}/${type}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getLatestTypedBalanceEntry(userid, formid, type) {
    return this.httpService.get(`api/Vault/latest-balance/${userid}/${formid}/${type}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getTransactionStatus(userid, formid) {
    return this.httpService.get(`api/Vault/transaction-status/${userid}/${formid}`, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  updateTaskReminderList(request) {
    const url = `api/checklist/task-reminder-owner`;
    return this.httpService.patch(url, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  updateReminder(request) {
    const url = `api/vault/vault-user-form-reminder`;
    return this.httpService.patch(url, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  updateReminderList(request) {
    const url = `api/vault/vault-user-form-reminder-list`;
    return this.httpService.patch(url, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }
  getTriggerTypeList() {
    const url = `api/vault/vault-user-reminder-setting`;
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        }
      })
    );
  }
  markAsCompleteReminder(request) {
    const url = `api/vault/vault-user-form-reminder-complete`;
    return this.httpService.post(url, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  grantPermission(request) {
    const url = `api/vault/vault-user-form-access-grant`;
    return this.httpService.post(url, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        }
      })
    );
  }
  addReminder(request) {
    const url = `api/vault/vault-user-form-reminder`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }
  checkUserHasFormAccess(userid, formid) {
    const data = {
      formids: formid,
      user_id: userid,
    };
    return this.httpService.post(`api/Vault/vault-user-form-access`, data, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  gatTransactionCount(userid, formid) {
    return this.httpService.get(`api/Vault/transaction-linked-count/${userid}/${formid}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getReportTransactionCount(id): Observable<any> {
    return this.httpService.get<any>(`api/vault/transaction-count-report/${id}`, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAllFilesStatus(userid, currentfolderid) {
    return this.httpService.get(`api/Vault/file-summary-by-status/${userid}/${currentfolderid}`, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  getAllTaskStatus(userid, currentfolderid) {
    return this.httpService.get(`api/task/task-summary/vault/${userid}/${currentfolderid}`, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getPermissionForGrantee(uid) {
    return this.httpService.get(`api/permission/admin-permission-given/${uid}`, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getFormActivity(id) {
    return this.httpService.get(`api/vault/vault-user-form-activity/${id}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getFolderActivity(id) {
    return this.httpService.get(`api/vault/vault-user-folder-activity/${id}`, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  onCloseDropdowns(close) {
    this.closeDropdowns.next(close);
  }

  getClosingDropdowns() {
    return this.closeDropdowns.asObservable();
  }

  getFolderHierarchy(userId, vaultUserFolderId, mydata = false) {
    let url = `api/vault/folder-hierachy/${userId}/${vaultUserFolderId}`;
    if (mydata) {
      url = url + '?myData=true';
    }
    return this.httpService.get(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getFolderHierarchyShared(userId, vaultUserFolderId) {
    const url = `api/vault/folder-hierachy-shared/${userId}/${vaultUserFolderId}`;
    return this.httpService.get(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getFolderDetails(userId, vaultUserFolderId, mydata = false) {
    let w_id = this.getWorSpaceId();
    let url = `api/vault/folder-details/${userId}/${vaultUserFolderId}`;
    if (mydata) {
      url = url + '?myData=true';
      if (w_id != null) {
        url = url + `&w_id=${w_id}`;
      }
    }

    return this.httpService.get(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getUserPublicKeyByFormId(vaultUserFormId) {
    const url = `api/vault/get-user-publickey-by-form/${vaultUserFormId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        }
      })
    );
  }
  getAllReminderForUser(userId) {
    const url = `api/vault/checklist/${userId}`;
    return this.httpService.get(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        }
      })
    );
  }

  getFormShareLink(userId, formId) {
    const url = `api/vault/vault-user-form-link-pin/${userId}/${formId}`;
    return this.httpService.get(url, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultUserFormLink(request) {
    const url = `api/vault/vault-user-form-link-pin`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.files;
        } else {
          return response;
        }
      })
    );
  }

  sendShareLinkEmail(request) {
    const url = `api/vault/vault-user-form-link-pin-email`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.files;
        } else {
          return response;
        }
      })
    );
  }

  updateUsePin(request) {
    const url = `api/vault/vault-user-form-link-use-pin`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.files;
        } else {
          return response;
        }
      })
    );
  }

  checkIfPinIsNeeded(code) {
    const url = `api/vault/vault-user-form-check-pin-needed/${code}`;
    return this.httpService.get(url, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getDataFromPin(code, pin) {
    const url = `api/vault/vault-user-form-via-link-pin/${code}/${pin}`;
    return this.httpService.get(url, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.files;
        } else {
          return response;
        }
      })
    );
  }

  duplicateTemplates(request) {
    const url = 'api/vault/vault-user-form-copy-template';
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getLocationFormAssociation(userid, vaultuserformid, vaultId = null) {
    let url = `api/vault/forms-user-location/${userid}/${vaultuserformid}`;
    if (vaultId != null) {
      url = url + `?vault_id=${vaultId}`;
    }
    return this.httpService.get(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getFormsForOwnedAssets(vaultUserId, formId, vaultId = null) {
    let url = `api/vault/forms-user-owned-assets/${vaultUserId}/${formId}`;
    if (vaultId != null) {
      url = url + `?vault_id=${vaultId}`;
    }
    return this.httpService.get(url, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getFormsForOwnedEntities(vaultUserId, formId, vaultId = null) {
    let url = `api/vault/forms-user-owned-entities/${vaultUserId}/${formId}`;
    if (vaultId != null) {
      url = url + `?vault_id=${vaultId}`;
    }
    return this.httpService.get(url, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createNote(payload: { vault_user_form_id: number; note: string; title?: string }) {
    const url = 'api/vault/vault-user-form-note';
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  getNotes({ userId, vaultUserFormId }: { userId: number; vaultUserFormId: number }) {
    const url = `api/vault/vault-user-form-note/${userId}/${vaultUserFormId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  deleteNote({ userId, noteId }: { userId: number; noteId: number }) {
    const url = `api/vault/vault-user-form-note/${userId}/${noteId}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateNote(payload, selectedSecurity_key = null) {
    let encryption_key;
    if (selectedSecurity_key == null) {
      encryption_key = this.cryptoService.generateEncryptionKey(payload.user_pk, null, null);
    } else {
      encryption_key = this.cryptoService.generateEncryptionKey(payload.user_pk, selectedSecurity_key);
    }
    payload.title = JSON.parse(payload.note).title ?? '';
    payload.note = this.cryptoService.encryptData(JSON.stringify(payload.note), encryption_key);
    payload.note = JSON.stringify(payload.note);
    const url = 'api/vault/vault-user-form-note';
    return this.httpService.patch(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  getSuggestionForNote({ userId, vaultUserFormId }: { userId: string; vaultUserFormId: string }, loaderType = loaderTypes.mainAnimation) {
    const url = `api/vault/form-note-type/${userId}/${vaultUserFormId}`;
    return this.httpService.get(url, null, loaderType).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  deleteFormImage({ userId, vaultFormId }) {
    const url = `api/vault/form-upload-profile/${userId}/${vaultFormId}`;
    return this.httpService.delete(url, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultUserFormFinancialAttribute(userId, vaultUserFormId) {
    const url = `api/vault/vault_user_form_financial_attribute/${userId}/${vaultUserFormId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createVaultUserFormFinancialAttribute(request) {
    const url = 'api/vault/vault_user_form_financial_attribute';
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultUserFormFinancialAttribute(request) {
    const url = 'api/vault/vault_user_form_financial_attribute';
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormFinancialAttribute(userId, financialId) {
    const url = `api/vault/vault_user_form_financial_attribute/${userId}/${financialId}`;
    return this.httpService.delete(url).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultUserFormTransactionRegisterStatus(request) {
    let url = `api/vault/vault-user-form-transaction-switcher`;
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAllTransactionRegisterAccounts(reportId): Observable<any> {
    const url = `api/vault/transaction-accounts/${reportId}`;
    return this.httpService.get<any>(url).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getTransactionFilterOptions(userId, reportId): Observable<any> {
    const url = `api/vault/transaction-filters/${userId}/${reportId}`;
    return this.httpService.get<any>(url).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getLifeInsuranceTransaction(userId, vaultUserFormId, pageNumber): Observable<any> {
    let url = `api/vault/balance/${userId}/${vaultUserFormId}/${pageNumber}/life_insurance`;
    return this.httpService.get<any>(url).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getLatestLifeInsuranceBalance(userId, formId) {
    const url = `api/vault/latest-balance/${userId}/${formId}/life_insurance`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getItemsToSetUpList(userid, formid) {
    const url = `api/vault/vault-user-form-setup-data/${userid}/${formid}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateItemsToSetUpList(request) {
    const url = `api/vault/vault-user-form-setup-data`;
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateItemsToSetUpLinkedItemComplete(request) {
    const url = `api/vault/vault-user-form-template-all-setup-data-complete`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateItemsToSetUpListStatusToFinish(payload: { formId: string; userId: string }) {
    const url = `api/vault/vault-user-form-finish-setup-data`;
    return this.httpService.patch(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getVaultFormRapidIntegration(id) {
    const url = `api/vault/get-vault-form-rapid-integration/${id}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  createVaultFormRapidIntegration(request) {
    const url = `api/vault/create-vault-form-rapid-integration`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateVaultFormRapidIntegration(request, id) {
    const url = `api/vault/update-vault-form-rapid-integration/${id}`;
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  deleteVaultFormRapidIntegration(formId) {
    const url = `api/vault/delete-vault-form-rapid-integration/${formId}`;
    return this.httpService.delete(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getFormsLinkingSearch(data) {
    let url = `api/vault/get-forms-linked-search`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getFormsLinkingSearchItemTOSetupLocation(data) {
    let url = `api/vault/get-forms-linked-to-location`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getFormsLinkingSearchLocation(data) {
    let url = `api/vault/get-forms-linked-to-location-search`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateVaultUserFormHoldingStatus(data) {
    let url = `api/vault/update-vault-user-form-holding-status`;
    return this.httpService.patch(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getVaultUserFormHoldingStatus(id) {
    let url = `api/vault/get-vault-user-form-holding-status/${id}`;
    return this.httpService.get(url, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getFormsLinkedDocuments(data) {
    let url = `api/vault/get-forms-linked-documents`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getFormsLinked(data) {
    let url = `api/vault/get-forms-linked`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getFormsLinkedLocation(data) {
    let url = `api/vault/get-location-linked`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getFormsLinkedLocationMultipleForm(data) {
    let url = `api/vault/get-location-linked-for-multiple-form`;
    return this.httpService.post(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getUserFormsForRolePlayersSearch(request) {
    const url = `api/vault/get-forms-role-player-search`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getUserFormsItemsOwnedBySearch(vaultUserId, vaultuserformid) {
    let url = `api/vault/forms-items-owned-by-search/${vaultUserId}/${vaultuserformid}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultUserFormSettingsForRolePlayers(request) {
    const url = `api/vault/get-vault-user-form-role-players-setting`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createVaultUserFormSettingsForRolePlayers(request) {
    const url = `api/vault/vault-user-form-role-players-setting`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateAddDocumentActiveStatus(request, loadertype = loaderTypes.mainAnimation) {
    const url = `api/vault/forms-user-deactivate-date`;
    return this.httpService.patch(url, request, loadertype).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }

  getSharedWithRecentFiles() {
    let url = `api/vault/shared-with-me-recent-files`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
  getSharedWithMeUsersFiles(category?: string) {
    let url = `api/vault/shared-with-me-users-files${category ? '/' + category : ''}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getreminderObjectlist(id, currentFormId) {
    let url = `api/vault/vault-user-form-reminder-object_list/${id}}/${currentFormId}`;
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAttachmentSuggestionBySubItemId(inboxId) {
    let url = `api/vault/inbox-attachment-suggestion-by-sub-item-id/${inboxId}`;
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAttachmentSuggestionById(inboxId) {
    let url = `api/vault/inbox-attachment-suggestion-by-id/${inboxId}`;
    return this.httpService.get(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateMultipleItemsToSetUpList(data: { ids: Array<string>; status: string | 'complete' }) {
    const url = `api/vault/vault-user-form-setup-data-multiple`;
    return this.httpService.patch(url, data, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getWorSpaceId() {
    if (this.router.url.includes('workspace')) {
      let splitUrl = this.router.url.split('/');
      return splitUrl?.length > 2 ? splitUrl[2] : null;
    } else {
      return null;
    }
  }
  getVaultId() {
    if (this.router.url.includes('vault')) {
      let splitUrl = this.router.url.split('/');
      if (this.router.url.includes('workspace')) {
        if (this.router.url.includes('mydata')) {
          return splitUrl?.length > 5 ? splitUrl[5] : null;
        } else {
          return splitUrl?.length > 4 ? splitUrl[4] : null;
        }
      }
      if (this.router.url.includes('mydata') && splitUrl?.length > 2) {
        return splitUrl[3];
      } else {
        return splitUrl?.length > 2 ? splitUrl[2] : null;
      }
    } else {
      return null;
    }
  }
  getMostRecentVaultUserForms(request?: any, mydata = false) {
    let w_id = this.getWorSpaceId();
    let url = `api/vault/most-recent-vault-user-forms`;
    if (mydata) {
      url = url + '?myData=true';
      if (w_id != null) {
        url = url + `&w_id=${w_id}`;
      }
    } else {
      if (w_id != null && request == null) {
        url = url + `?w_id=${w_id}`;
      }
    }
    return this.httpService.get(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        }
      })
    );
  }
  checkFormIsblank(id) {
    const url = `api/vault/vault-user-form-blank/${id}`;
    return this.httpService.get<any>(url, null, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getGuardianUserForVaultMember() {
    const url = `api/vault/non-vault-member-guardian-user`;
    return this.httpService.get<any>(url, null, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultUserFormBalanceDetailsAndAlerts(reportExecutionId): Observable<any> {
    const url = `api/vault/account-balance-details/${reportExecutionId}`;
    return this.httpService.get<any>(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createVaultUserFormBalanceAlert(request) {
    const url = `api/vault/vault-user-form-balance-alert`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultUserFormBalanceAlert(request) {
    const url = `api/vault/vault-user-form-balance-alert`;
    return this.httpService.patch(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultForVaultUserForm(request) {
    const url = `api/vault/update-vault-for-vault-user-form`;
    return this.httpService.post(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  deleteVaultUserFormBalanceAlert(userId, alertId) {
    const url = `api/vault/vault-user-form-balance-alert/${userId}/${alertId}`;
    return this.httpService.delete(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getInsuranceDropDownList(): Observable<any> {
    const url = `api/vault/attributes-dropdown-list`;
    return this.httpService.get<any>(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getVaultUserFormVaultSuggestion(formId): Observable<any> {
    const url = `api/vault/vault-user-form-vault-suggestion/${formId}`;
    return this.httpService.get<any>(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getInsuranceAttribute(): Observable<any> {
    const url = `api/vault/insurance-attributes`;
    return this.httpService.get<any>(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getItemToResolvedSidebarItems(type, reportId = null): Observable<any> {
    const url = reportId ? `api/onboarding/items-to-setup-sub-types/${type}/${reportId}` : `api/onboarding/items-to-setup-sub-types/${type}`;
    return this.httpService.get<any>(url, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getItemToResolvedSidebarSections(type, sub_type, reportId = null): Observable<any> {
    const url = reportId ? `api/onboarding/items-to-setup/${type}/${sub_type}/${reportId}` : `api/onboarding/items-to-setup/${type}/${sub_type}`;
    return this.httpService.get<any>(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getFinancialAttribute(): Observable<any> {
    const url = `api/vault/financial-attributes`;
    return this.httpService.get<any>(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateSetVaultPreference(data) {
    const url = `api/vault/inbox-update-vault`;
    return this.httpService.post<any>(url, data, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateMarkComplete(data): Observable<any> {
    const url = `api/vault/vault-user-form-multiple-setup-data-complete`;
    return this.httpService.post<any>(url, data, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  undoMarkComplete(data): Observable<any> {
    const url = `api/vault/vault-user-form-setup-data-multiple-undo`;
    return this.httpService.patch<any>(url, data, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getFormSummaryData(currentFormId): Observable<any> {
    const url = `api/vault/vault-user-form-summary/${currentFormId}`;
    return this.httpService.get<any>(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultUserFormSummary(id): Observable<any> {
    const url = `api/vault/vault-user-form-summary/${id}`;
    return this.httpService.patch<any>(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  checkRolePlayersCircularOwnership(request) {
    const url = `api/vault/check-role-players-circular-ownership`;
    return this.httpService.post(url, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  gatLatestDistributionBalance(userid, formid, type?: string) {
    return this.httpService.get(`api/Vault/latest-distribution-balance/${userid}/${formid}/${type}`, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  createVaultUserTag(payload) {
    const url = `api/vault/vault-user-tag`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserTag(payload) {
    const url = `api/vault/delete-vault-user-tag`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  removeInboxDocumentSuggestionTag(payload) {
    const url = `api/vault/remove-document-suggestion-tag`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getSharedByMeData(category_id = null, userId) {
    const url = `api/vault/shared-by-me-users-files/${category_id}/${userId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  getNonDocumentSuggestions(formId) {
    const url = `api/vault/form-non-linking-suggestions/${formId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }
  getSharedWithMeData(category_id = null, userId) {
    const url = `api/vault/shared-with-me-users-files/${category_id}/${userId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }
  deleteAllSharedFileByUserId(sharedWithUserId) {
    const url = `api/vault/unshare-all-files/${sharedWithUserId}`;
    return this.httpService.delete(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  updateVaultFormTemplateStatus(payload) {
    const url = `api/vault/template-status`;
    return this.httpService.patch(url, payload, loaderTypes.none).pipe(
      map((response: any) => {
        return response;
      })
    );
  }
  getItemToSetup(type, sub_type?: string): Observable<any> {
    const url = sub_type ? `api/onboarding/items-to-setup/${type}/${sub_type}` : `api/onboarding/items-to-setup/${type}`;
    return this.httpService.get<any>(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAllUserFormsForLinkedPositions(payload) {
    const url = `api/vault/get-user-forms-for-linked-positions`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultUserFormLinkedPositions(userId: string, formId: string, source?: string) {
    const url = source
      ? `api/vault/vault-user-form-linked-positions/${userId}/${formId}/${source}`
      : `api/vault/vault-user-form-linked-positions/${userId}/${formId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createVaultUserFormLinkedPositions(payload) {
    const url = `api/vault/vault-user-form-linked-positions`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormLinkedPositions(payload) {
    const url = `api/vault/delete-vault-user-form-linked-positions`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateVaultUserFormLinkedPositionsSetting(payload) {
    const url = `api/vault/vault-user-form-linked-positions-setting`;
    return this.httpService.patch(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultUserFormBeneficialOwnership(userId, formId, source?: string) {
    const url = source
      ? `api/vault/vault-user-form-beneficial-ownership/${userId}/${formId}/${source}`
      : `api/vault/vault-user-form-beneficial-ownership/${userId}/${formId}`;
    return this.httpService.get(url, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultUserFormBeneficialOwnership(payload) {
    const url = `api/vault/delete-vault-user-form-beneficial-ownership`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAllUserTrustForms(request) {
    const url = `api/vault/vault-user-trust-forms`;
    return this.httpService.post<any>(url, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteVaultSuggestionById(payload) {
    const url = `api/vault/vault-suggestion-remove`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  createDiretDeliveryConnection(payload) {
    const url = `api/user/create-direct-delivery`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getDiretDeliveryConnection() {
    const url = `api/user/direct-delivery`;
    return this.httpService.get(url, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  removeDiretDeliveryConnection(payload) {
    const url = `api/user/delete-direct-delivery`;
    return this.httpService.post(url, payload, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getVaultSuggestionByUser() {
    const url = `api/vault/vault-suggestion`;
    return this.httpService.get(url, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getFinancialAccounts() {
    return this.httpService.get(`api/Vault/my-financial-accounts`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }
}
